<template>
  <section id="dashboard">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <div class="page-inner">
      <div class="content-panel">
        <div class="filter-bar">
          <h1 class="page-title">Lojas</h1>

          <div class="topbar-search">
            <b-autocomplete
              v-model="storeInput"
              :data="filteredOptions"
              field="name"
              placeholder="Buscar loja ou CNPJ..."
              clearable
              open-on-focus
              @input="onInput"
              @select="onStoreSelect"
            >
              <template slot-scope="props">
                <div v-if="props.option.type === 'all'" class="option-all">
                  <span class="material-icons option-all-icon">store</span>
                  <span>{{ props.option.name }}</span>
                </div>
                <div
                  v-else-if="props.option.type === 'group'"
                  class="option-group"
                >
                  <span class="option-group-cnpj">{{
                    formatCnpj(props.option.cnpj)
                  }}</span>
                  <span
                    v-if="props.option.companyName"
                    class="option-group-company"
                    >{{ props.option.companyName }}</span
                  >
                  <span class="option-group-count"
                    >{{ props.option.storeCount }} loja{{
                      props.option.storeCount !== 1 ? "s" : ""
                    }}</span
                  >
                </div>
                <div v-else class="option-store">
                  <span>{{ props.option.name }}</span>
                  <span v-if="props.option.cnpj" class="option-cnpj-hint">{{
                    formatCnpj(props.option.cnpj)
                  }}</span>
                </div>
              </template>
              <template slot="empty">Nenhuma loja encontrada</template>
            </b-autocomplete>
          </div>
          <!-- filtro de ordem oculto -->
        </div>

        <div
          v-if="filteredMerchants.length === 0 && !isLoading"
          class="empty-state"
        >
          Nenhuma loja encontrada.
        </div>

        <merchant-group
          v-for="merchant in filteredMerchants"
          :key="merchant._id"
          :cnpj="merchant.companyInfo && merchant.companyInfo.nationalCompanyId"
          :company-name="
            merchant.companyInfo && merchant.companyInfo.companyName
          "
          :stores="merchant.stores"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import merchantGroup from "./MerchantGroup";

export default {
  mixins: [common],

  components: {
    merchantGroup
  },

  data() {
    return {
      isLoading: false,
      storeInput: "", // texto exibido no autocomplete
      searchQuery: "" // filtro real aplicado em filteredMerchants
    };
  },

  computed: {
    ...mapGetters(["address", "companyInfo", "merchants", "preferences"]),

    filteredOptions() {
      const q = this.storeInput.toLowerCase().trim();
      const allOption = { type: "all", name: "Todas as lojas" };

      const groupOptions = this.merchants
        .filter(m => m.stores && m.stores.length > 0)
        .map(m => ({
          type: "group",
          name: this.formatCnpj(
            (m.companyInfo && m.companyInfo.nationalCompanyId) || ""
          ),
          cnpj: (m.companyInfo && m.companyInfo.nationalCompanyId) || "",
          companyName: (m.companyInfo && m.companyInfo.companyName) || "",
          storeCount: m.stores.length
        }));

      const storeOptions = this.merchants
        .flatMap(m =>
          (m.stores || []).map(s => ({
            type: "store",
            name: s.storeInfo.name,
            cnpj: (m.companyInfo && m.companyInfo.nationalCompanyId) || ""
          }))
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      if (!q || "todas as lojas".includes(q)) {
        return [allOption, ...groupOptions, ...storeOptions];
      }

      const cleanQ = q.replace(/\D/g, "");

      const matchingGroups = groupOptions.filter(g => {
        const label = `${this.formatCnpj(g.cnpj)} ${
          g.companyName
        }`.toLowerCase();
        return (
          label.includes(q) ||
          (cleanQ && g.cnpj.replace(/\D/g, "").includes(cleanQ))
        );
      });

      const matchingStores = storeOptions.filter(s =>
        s.name.toLowerCase().includes(q)
      );

      return [allOption, ...matchingGroups, ...matchingStores];
    },

    filteredMerchants() {
      const q = this.searchQuery.toLowerCase().trim();
      const cleanQ = q.replace(/\D/g, "");
      const alphabetical =
        this.preferences && this.preferences.storesOrder === "alphabetical";

      return this.merchants
        .map(merchant => {
          let stores = merchant.stores || [];

          if (q) {
            stores = stores.filter(s => {
              const name = (s.storeInfo.name || "").toLowerCase();
              const cnpj =
                (merchant.companyInfo &&
                  merchant.companyInfo.nationalCompanyId) ||
                "";
              const companyName = (
                (merchant.companyInfo && merchant.companyInfo.companyName) ||
                ""
              ).toLowerCase();
              return (
                name.includes(q) ||
                companyName.includes(q) ||
                (cleanQ && cnpj.replace(/\D/g, "").includes(cleanQ))
              );
            });
          }

          if (alphabetical) {
            stores = [...stores].sort((a, b) =>
              a.storeInfo.name.localeCompare(b.storeInfo.name)
            );
          }

          return { ...merchant, stores };
        })
        .filter(merchant => merchant.stores.length > 0)
        .sort((a, b) => {
          const nameA = (a.companyInfo && a.companyInfo.companyName) || "";
          const nameB = (b.companyInfo && b.companyInfo.companyName) || "";
          return nameA.localeCompare(nameB);
        });
    }
  },

  methods: {
    formatCnpj(cnpj) {
      if (!cnpj) return "";
      const c = cnpj.replace(/\D/g, "");
      if (c.length !== 14) return cnpj;
      return c.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    onInput(value) {
      // Digitação livre — filtra pelo texto, ignora o texto da opção "Todas"
      if (!value || value === "Todas as lojas") {
        this.searchQuery = "";
      } else {
        this.searchQuery = value;
      }
    },

    onStoreSelect(option) {
      if (!option || option.type === "all") {
        this.storeInput = "";
        this.searchQuery = "";
      } else if (option.type === "group") {
        this.searchQuery = option.cnpj;
      } else {
        this.searchQuery = option.name;
      }
    },

    getStores() {
      const today = new Date();
      const year = today.getFullYear();
      let month = today.getMonth() + 1;
      month = month < 10 ? "0" + month : month;
      let day = today.getDate();
      day = day < 10 ? "0" + day : day;
      const date = `${year}-${month}-${day}`;

      this.isLoading = true;
      this.$store
        .dispatch("getStores", date)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateStoresOrderPreference() {
      this.$store
        .dispatch("updateStoresOrderPreference", {
          storesOrder: this.preferences.storesOrder
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        });
    }
  },

  mounted() {
    this.$store.dispatch("setStoreBeingManaged", null).catch(() => {
      this.alertError({ message: "UNEXPECTED_ERROR" });
    });
    this.getStores();
  }
};
</script>

<style scoped>
#dashboard {
  min-height: 100vh;
  background-color: #f8f7fa;
  padding: 1.5rem;
}

.page-inner {
  width: 100%;
}

.content-panel {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #f3f4f6;
  padding: 1.5rem;
}

/* ── barra de filtro ── */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #4a4453;
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── autocomplete (cresce) ── */
.topbar-search {
  flex: 0 0 50%;
  min-width: 0;
}

.topbar-search ::v-deep .input {
  border-radius: 8px;
  border-color: #e0e0e0;
  font-size: 0.875rem;
  height: 38px;
}

/* ── opções do autocomplete ── */
.option-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #ff5c1a;
  padding: 2px 0;
}

.option-all-icon {
  font-size: 1rem;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.option-group-cnpj {
  color: hsl(0, 0%, 13%);
}
.option-group-company {
  font-size: 0.82rem;
  color: hsl(0, 0%, 45%);
  font-weight: 400;
}
.option-group-count {
  margin-left: auto;
  font-size: 0.75rem;
  color: hsl(0, 0%, 55%);
  font-weight: 400;
}

.option-store {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.option-cnpj-hint {
  font-size: 0.8rem;
  color: hsl(0, 0%, 55%);
}

.empty-state {
  padding: 3rem 0;
  color: #9ca3af;
  text-align: center;
}

@media (max-width: 600px) {
  .topbar-search {
    flex: 1 1 100%;
  }
}
</style>
